import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isEqualWith, isNull } from "lodash";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { faUsersSlash } from "@fortawesome/pro-solid-svg-icons";

import { StatusCode } from "api/protocols";
import { GetCashOuts, GetCashOutSummary, UpdateCashOut } from "api/rpc/2024-04/facilityAdmin/facility/register/shift";

import { IUIActions } from "redux/actions/ui";
import { IUserPermissions } from "redux/reducers/models/user";

import { IRegister } from "../../Admin";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Page from "components/page/Page";
import Input from "components/form/input/Input";
import TextField from "components/form/textField/TextField";
import { Select } from "components/select/index";
import { formatDate, isNumeric } from "helpers/Helpers";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { GetFacilityAdmin } from "api/rpc/facilityAdmin/client/admin";
import moment, { MomentInput } from "moment";
import Icon from "components/icon/Icon";
import { ICashOut } from "api/rpc/2022-09/facilityAdmin/facility/register/cashOut";
import { GetRegisters } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";

interface ICashOutEditProps {
  uiActions: IUIActions;
  permissions: IUserPermissions;
}

interface ICashOutEditState {
  cashOut: ICashOut;
  cashOutBeforeChanges: ICashOut;
  cashOutLoaded: boolean;
  depositCashInput: string;
  depositCheckInput: string;
  expectedDepositCash: string;
  expectedDepositCheck: string;
  depositVariance: string;
  registers: IRegister[];
  deposit_date: Date;
  deposited_by: number;
  deposited_by_name: string;
  closed_at: Date;
  blind_close: boolean;
}

interface IParams {
  cashOutId: string;
}

interface IFilterState {
  adminSearch: string;
  searchResults: any[];
  deposit_date?: Date;
  showDepositInfo: boolean;
  showDepositInfoButton: boolean;
}

export default function CashOutEdit(props: ICashOutEditProps) {
  const { cashOutId } = useParams<IParams>();
  const history = useHistory();

  const { uiActions, permissions } = props;

  const { Option } = Select;

  const { t } = useTranslation();

  const [cashOutEditState, setCashOutEditState] = useState<ICashOutEditState>({
    cashOut: undefined,
    cashOutBeforeChanges: undefined,
    cashOutLoaded: false,
    depositCashInput: "",
    depositCheckInput: "",
    expectedDepositCheck: "",
    expectedDepositCash: "",
    depositVariance: "",
    registers: [],
    deposit_date: new Date(),
    deposited_by: null,
    deposited_by_name: "",
    closed_at: null,
    blind_close: false,
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    adminSearch: "",
    searchResults: [],
    showDepositInfo: false,
    showDepositInfoButton: false,
  });

  const [voidPopupState, setVoidPopupState] = useState(false);

  useEffect(() => {
    void loadCashOut();
    void loadRegisters();
    // void loadAdminUsers();
  }, []);

  async function loadCashOut() {
    const getCashOutsResponse = await GetCashOuts({ id: parseInt(cashOutId) }, true);

    if (getCashOutsResponse.status !== StatusCode.OK) {
      uiActions.showError(t("Failed to load cash out"));
      return;
    }

    const cashOut = getCashOutsResponse.data?.[0];

    const tempClosedDate = moment.utc(String(cashOut.closed_at) + "Z").local().toDate();

    let tempDateString = "";
    let tempDate = new Date();
    let showDepositInfo = false;
    let showDepositInfoButton = true;

    if (cashOut.deposit_date !== null && cashOut.deposit_date !== undefined) {
      tempDateString = moment(cashOut.deposit_date).format();
      tempDate = new Date(tempDateString);
      showDepositInfo = true;
      showDepositInfoButton = false;
    }

    const defaultSearch = "";

    const usersRes = await GetFacilityAdmin(null, true);

    if (usersRes.status !== StatusCode.OK) {
      uiActions.showError(usersRes.message);
      return;
    }

    setCashOutEditState(prev => ({
      ...prev,
      cashOut: cashOut,
      cashOutLoaded: true,
      depositCashInput: String(cashOut?.deposit_cash),
      depositCheckInput: String(cashOut?.deposit_check),
      expectedDepositCash: cashOut?.deposit_cash_expected ? String(cashOut?.deposit_cash_expected) : "0.00",
      expectedDepositCheck: cashOut?.deposit_check_expected ? String(cashOut?.deposit_check_expected) : "0.00",
      depositVariance: cashOut?.deposit_variance ? String(cashOut?.deposit_variance) : "0.00",
      deposited_by: cashOut.deposited_by,
      deposit_date: tempDate,
      deposited_by_name: defaultSearch,
      closed_at: tempClosedDate,
      blind_close: cashOut.blind_close,
    }));

    setFilterState(prevState => ({
      ...prevState,
      deposit_date: tempDate,
      showDepositInfo: showDepositInfo,
      showDepositInfoButton: showDepositInfoButton,
      searchResults: usersRes.data,
      adminSearch: defaultSearch,
    }));
  }

  async function loadRegisters() {
    const getRegistersResponse = await GetRegisters({}, true);

    if (getRegistersResponse.status !== StatusCode.OK) {
      uiActions.showError(t("Failed to get registers"));
      return;
    }

    setCashOutEditState(prevState => ({ ...prevState, registers: getRegistersResponse.data }));
  }

  function handleValidatedCashOutChange(event: any, cashOutChange: Partial<ICashOut>) {
    const { id, value } = event.target;
    setCashOutEditState(prev => ({
      ...prev,
      [id]: value,
      cashOut: {
        ...prev.cashOut,
        ...cashOutChange,
      },
    }));
  }

  function handleCashOutInputChange(event: any) {
    const { id, value } = event.target;
    setCashOutEditState(prev => ({
      ...prev,
      cashOut: {
        ...prev.cashOut,
        [id]: value,
      },
    }));
  }

  function handleCashOutRegisterChange(register_id: number) {
    setCashOutEditState(prev => ({
      ...prev,
      cashOut: {
        ...prev.cashOut,
        register_id,
      },
    }));
  }

  function unsavedChangesExist() {
    if (cashOutEditState.cashOutBeforeChanges === undefined) {
      if (cashOutEditState.cashOutLoaded) {
        setCashOutEditState(prev => ({
          ...prev,
          cashOutBeforeChanges: prev.cashOut,
        }));
      }

      return false;
    }

    return !isEqualWith(cashOutEditState.cashOutBeforeChanges, cashOutEditState.cashOut, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function cancelUnsavedChanges() {
    console.log(cashOutEditState);
    console.log(filterState);

    const tempDateString = moment(filterState.deposit_date).format();
    const tempDate = new Date(tempDateString);

    setCashOutEditState(prev => ({
      ...prev,
      cashOut: prev.cashOutBeforeChanges,
      depositCashInput: String(prev.cashOutBeforeChanges.deposit_cash),
      depositCheckInput: String(prev.cashOutBeforeChanges.deposit_check),
      deposit_date: tempDate,
    }));

    if (filterState.showDepositInfoButton) {
      setFilterState(prevState => ({ ...prevState, showDepositInfo: false, adminSearch: "" }));
    }
  }

  async function printCashOutSummary() {
    const res = await GetCashOutSummary(
      {
        register_shift_id: Number(cashOutId),
      },
      true,
    );

    if (res.status === StatusCode.OK) {
      window.open().document.write(String(res.data));
    } else {
      uiActions.showError(t("Error getting summary"));
    }
  }

  function saveCashout() {
    void updateCashOut();
  }

  const primaryAction = {
    content: t("Save"),
    action: saveCashout,
  };

  function handleDateSelector(deposit_date: Date) {
    setCashOutEditState(prev => ({
      ...prev,
      cashOut: {
        ...prev.cashOut,
        deposit_date,
      },

      deposit_date: deposit_date,
    }));
  }

  async function handleVoid() {
    const updateRes = await UpdateCashOut(
      {
        register_shift_id: parseInt(cashOutId),
        status: "voided",
      },
      true,
    );

    if (updateRes.status !== StatusCode.OK) {
      uiActions.showError(updateRes.message);
      return;
    }

    setVoidPopupState(false);
    uiActions.showSuccess(t("Cash out voided successfully"));
    history.push("/admin/settings/cash-out");
  }

  async function updateCashOut() {
    const params = {
      deposited_by: cashOutEditState.cashOut.deposited_by,
      deposit_date: formatDate(cashOutEditState.deposit_date),
      register_shift_id: parseInt(cashOutId),
      deposit_cash: parseFloat(cashOutEditState.depositCashInput),
      deposit_check: parseFloat(cashOutEditState.depositCheckInput),
      deposit_bag_number: cashOutEditState.cashOut.deposit_bag_number,
      notes: cashOutEditState.cashOut.notes,
      register_id: cashOutEditState.cashOut.register_id,
    };

    const updateRes = await UpdateCashOut(params, true);

    if (updateRes.status !== StatusCode.OK) {
      uiActions.showError(updateRes.message);
      return;
    }

    const cashOut = updateRes.data;

    let tempDateString = "";
    let tempDate = new Date();

    if (cashOut.deposit_date !== null && cashOut.deposit_date !== undefined) {
      tempDateString = moment(cashOut.deposit_date as MomentInput).format();
      tempDate = new Date(tempDateString);
    }

    uiActions.showSuccess(t("Cash out saved successfully"));

    setFilterState(prevState => ({ ...prevState, deposit_date: cashOutEditState.cashOut.deposit_date }));
    setCashOutEditState(prev => ({
      ...prev,
      cashOut: cashOut,
      cashOutBeforeChanges: cashOut,
      depositCashInput: String(cashOut?.deposit_cash),
      depositCheckInput: String(cashOut?.deposit_check),
      deposited_by: cashOut.deposited_by,
      deposit_date: tempDate,
    }));
  }

  function handleCashOutDepositByChange(deposited_by: number, deposited_by_full_name: string) {
    setCashOutEditState(prev => ({
      ...prev,
      cashOut: {
        ...prev.cashOut,
        deposited_by,
        deposited_by_full_name,
      },
    }));
    setFilterState(prevState => ({ ...prevState, adminSearch: "" }));
  }

  const handleAdminSearch = (query: string) => {
    setFilterState(prevState => ({ ...prevState, adminSearch: query }));
  };

  return (
    <Page
      title={`${t("Cash Out")} ${cashOutEditState.cashOut?.closed_at
        ? moment.utc(String(cashOutEditState.cashOut.closed_at) + "Z").local().format("MMM DD, YYYY")
        : ""
        }`}
      narrow
      primaryAction={permissions.cash_outs_view_summary && primaryAction}
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("Print Summary"),
              icon: "print",
              handler: () => printCashOutSummary(),
            },
            {
              type: "handler",
              label: t("Void"),
              icon: "circle-x",
              handler: () => setVoidPopupState(true),
              disabled: !permissions.cash_outs_edit || cashOutEditState.cashOut?.status === "voided",
            },
          ],
        },
      }}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to cash outs"),
          url: "/admin/settings/cash-out",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveCashout,
        onCancel: cancelUnsavedChanges,
      }}
    >
      {cashOutEditState.cashOut && cashOutEditState.cashOutLoaded && (
        <>
          <Card>
            <Card.Section>
              <div className="flex items-center">
                <p className="text-medium text-gray-900 w-full">
                  {t("Deposit Cash Expected")}: <span className="text-medium text-gray-600">${cashOutEditState.expectedDepositCash}</span>
                </p>
                <p className="text-medium text-gray-900 w-full" style={{ marginLeft: "2rem" }}>
                  {t("Deposit Check Expected")}:{" "}
                  <span className="text-medium text-gray-600">${cashOutEditState.expectedDepositCheck}</span>
                </p>
                <p className="text-medium text-gray-900 w-full" style={{ marginLeft: "2rem" }}>
                  {t("Deposit Variance")}:{" "}
                  <span className="text-medium text-gray-600">${cashOutEditState.depositVariance}</span>
                </p>
              </div>
            </Card.Section>
          </Card>

          <Card>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    label={t("Closed By")}
                    onChange={() => {
                      return;
                    }}
                    value={cashOutEditState.cashOut.closed_by_full_name}
                    disabled={!permissions.cash_outs_edit}
                    readOnly
                  />
                  <Input
                    label={t("Closed On")}
                    onChange={() => {
                      return;
                    }}
                    value={
                      cashOutEditState.cashOut?.closed_at
                        ? moment.utc(String(cashOutEditState.cashOut.closed_at) + "Z").local().format("YYYY-MM-DD")
                        : ""
                    }
                    disabled={true}
                    readOnly
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <Input
                    value={cashOutEditState.depositCashInput}
                    label={t("Deposit Cash")}
                    id="depositCashInput"
                    onChange={(e: any) =>
                      handleValidatedCashOutChange(
                        e,
                        isNumeric(String(e.target.value)) ? { deposit_cash: parseFloat(String(e.target.value)) } : {},
                      )
                    }
                    error={!isNumeric(cashOutEditState.depositCashInput)}
                    disabled={!permissions.cash_outs_edit}
                    readOnly
                  />
                  <Input
                    value={cashOutEditState.depositCheckInput}
                    label={t("Deposit Check")}
                    id="depositCheckInput"
                    onChange={(e: any) =>
                      handleValidatedCashOutChange(
                        e,
                        isNumeric(String(e.target.value)) ? { deposit_check: parseFloat(String(e.target.value)) } : {},
                      )
                    }
                    error={!isNumeric(cashOutEditState.depositCheckInput)}
                    disabled={!permissions.cash_outs_edit}
                    readOnly
                  />
                </FormLayout.Group>

                <FormLayout.Group>
                  <Input
                    value={cashOutEditState.cashOut.deposit_bag_number}
                    label={t("Deposit Bag Number")}
                    id="deposit_bag_number"
                    onChange={handleCashOutInputChange}
                    disabled={!permissions.cash_outs_edit}
                    readOnly
                  />
                  <Select
                    label={t("Register")}
                    onChange={handleCashOutRegisterChange}
                    defaultValue={cashOutEditState.cashOut.register_id}
                    disabled={true}
                  >
                    {cashOutEditState.registers?.map((register, index) => {
                      return (
                        <Option key={index} value={register.id}>
                          {register.title}
                        </Option>
                      );
                    })}
                  </Select>
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField
                    value={cashOutEditState.cashOut.notes}
                    label={t("Notes")}
                    id="notes"
                    onChange={handleCashOutInputChange}
                    disabled={!permissions.cash_outs_edit}
                  />
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
          </Card>

          <Card title={t("Deposit")}>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <div className="flex align-items-end h-full">
                    <DatePickerInput
                      months={1}
                      position={"left"}
                      label={t("Deposited On")}
                      startingDate={cashOutEditState.deposit_date}
                      setStartingDate={handleDateSelector}
                    />
                  </div>

                  {cashOutEditState.cashOut?.deposited_by ? (
                    <div style={{ marginTop: "5px" }}>
                      <div>
                        <p className="event-label"> {t("Deposited By")} </p>
                      </div>

                      <div className="selected-container">
                        <div className="event-name">
                          <div>{cashOutEditState.cashOut?.deposited_by_full_name}</div>

                          <div>
                            <button
                              className=""
                              onClick={() =>
                                setCashOutEditState(prevState => ({
                                  ...prevState,
                                  cashOut: { ...prevState.cashOut, deposited_by: null, deposited_by_full_name: null },
                                }))
                              }
                            >
                              <Icon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Select
                      showSearch
                      onSearch={(query: string) => handleAdminSearch(query)}
                      onChange={(id: number, extraValues: string) => handleCashOutDepositByChange(id, extraValues)}
                      allowClear
                      searchValue={filterState.adminSearch}
                      label={t("Deposited By")}
                      placeholder={t("Users")}
                      defaultValue={cashOutEditState.cashOut.deposited_by}
                      dropDownPositionTop
                      noData={
                        <div className="px-4">
                          <div className="border-b flex items-center p-4 border-b mb-4">
                            <Icon icon={faUsersSlash} className="m-0" />
                            <span className="ml-3 italic">{t("No user found...")}</span>
                          </div>
                        </div>
                      }
                    >
                      {filterState.searchResults
                        .filter(result =>
                          result.full_name.toLowerCase().includes(filterState.adminSearch.toLowerCase()),
                        )
                        .map((user: any) => {
                          if (user !== null && user !== undefined) {
                            return (
                              <Option key={user?.id} value={user?.id} extraValues={user?.full_name}>
                                <div className="flex justify-between">
                                  <div>
                                    <div className="text-semibold text-lg">{user?.full_name}</div>
                                    <div className="text-sm text-gray-500">{user?.email}</div>
                                  </div>
                                </div>
                              </Option>
                            );
                          }
                        })}
                    </Select>
                  )}
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
          </Card>
        </>
      )}

      <Portal isMounted={voidPopupState}>
        <Popup
          open={voidPopupState}
          onOk={handleVoid}
          onCancel={() => setVoidPopupState(false)}
          okText={t("Void")}
          type="info"
          title={t("Void Cash Out?")}
          description={t("This cash out will be voided and removed from the cash outs list.")}
        />
      </Portal>
    </Page>
  );
}
